import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PackageOpen, Trash2, Save, Printer, FileText, Package, Plus, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import {
  listParts, lookupPartByNumber, createPackingSlip, getPackingSlip, updatePackingSlip,
  dispatchPackingSlip,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const blankItem = () => ({
  id: `tmp-${Date.now()}-${Math.random()}`,
  part_number: "",
  part_name: "",
  qty: 1,
  box_no: "",
});

// Sanitise box-no string: keep digits + commas, dedupe, pad to 2 digits
const normaliseBox = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim().replace(/\D/g, ""))
    .filter(Boolean)
    .map((s) => s.padStart(2, "0"))
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(",");

const NewPackingSlip = () => {
  const { currentUser, currentLocation, locations } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const allowedLocations = useMemo(
    () =>
      locations.filter(
        (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
      ),
    [locations, currentUser]
  );

  const defaultLoc = () => {
    if (currentLocation && currentLocation !== "ALL" && !currentLocation.includes(",")) {
      return currentLocation;
    }
    if (allowedLocations.length === 1) return allowedLocations[0].code;
    return allowedLocations[0]?.code || "";
  };

  const [invoiceNo, setInvoiceNo] = useState("");
  const [locationCode, setLocationCode] = useState(defaultLoc());
  const [toLocationName, setToLocationName] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [confirmDispatchOpen, setConfirmDispatchOpen] = useState(false);
  const [savedSlip, setSavedSlip] = useState(null); // {id, slip_number, dispatched}
  const [partsMaster, setPartsMaster] = useState([]);
  // Map keyed by normalised (upper+trim) part_number → master row.
  // Built from `partsMaster` so the autocomplete & exact-match lookup are O(1).
  const partsIndex = useMemo(() => {
    const m = new Map();
    for (const p of partsMaster) {
      const key = (p.part_number || "").trim().toUpperCase();
      if (key) m.set(key, p);
    }
    return m;
  }, [partsMaster]);
  // Per-row lookup state — "ok" | "missing" | "loading" | undefined
  const [rowLookupState, setRowLookupState] = useState({});
  // Pending server lookups, keyed by normalised part number → Promise.
  // Dedupes concurrent fetches for the same part across multiple rows.
  const lookupCacheRef = useRef(new Map()); // key → {part_name} or null (negative)
  const inflightRef = useRef(new Map()); // key → Promise

  // Refs for "Tab on last cell → new row + focus first input"
  const rowRefs = useRef([]);

  useEffect(() => {
    // Fetch the entire parts master once on mount (the new default limit is
    // 5000 — covers virtually any dealership inventory). The local map handles
    // sync lookups; a server-side fallback handles anything missing.
    listParts().then(setPartsMaster).catch(() => setPartsMaster([]));
  }, []);

  // Server-side exact lookup with caching + in-flight dedupe.
  const lookupOnServer = async (rawPartNo) => {
    const key = (rawPartNo || "").trim().toUpperCase();
    if (!key) return null;
    if (lookupCacheRef.current.has(key)) {
      return lookupCacheRef.current.get(key);
    }
    if (inflightRef.current.has(key)) {
      return inflightRef.current.get(key);
    }
    const p = lookupPartByNumber(key)
      .then((doc) => {
        lookupCacheRef.current.set(key, doc);
        return doc;
      })
      .catch(() => {
        lookupCacheRef.current.set(key, null); // negative cache (404)
        return null;
      })
      .finally(() => inflightRef.current.delete(key));
    inflightRef.current.set(key, p);
    return p;
  };

  // Resolve a single row's part name. Synchronous map first, server fallback
  // second. Always updates the row's state so the user sees feedback.
  const resolvePartName = async (rowId, rawPartNo) => {
    const key = (rawPartNo || "").trim().toUpperCase();
    if (!key) {
      setRowLookupState((s) => ({ ...s, [rowId]: undefined }));
      return;
    }
    // 1. Try the local index first (covers ~99% of cases when master fits)
    const localHit = partsIndex.get(key);
    if (localHit) {
      setItems((arr) =>
        arr.map((it) =>
          it.id === rowId ? { ...it, part_name: localHit.part_name } : it
        )
      );
      setRowLookupState((s) => ({ ...s, [rowId]: "ok" }));
      return;
    }
    // 2. Server fallback (handles late-loaded master, parts added mid-session, etc.)
    setRowLookupState((s) => ({ ...s, [rowId]: "loading" }));
    const doc = await lookupOnServer(key);
    if (doc) {
      setItems((arr) =>
        arr.map((it) =>
          it.id === rowId ? { ...it, part_name: doc.part_name } : it
        )
      );
      setRowLookupState((s) => ({ ...s, [rowId]: "ok" }));
      // Also enrich the in-memory cache so future rows hit locally.
      setPartsMaster((arr) => {
        if (arr.find((p) => (p.part_number || "").trim().toUpperCase() === key)) return arr;
        return [...arr, doc];
      });
    } else {
      setRowLookupState((s) => ({ ...s, [rowId]: "missing" }));
    }
  };

  // Once locations load, fix default if it's still empty.
  // Replaces an unnecessary useEffect that triggered react-hooks/set-state-in-effect.
  if (!locationCode && allowedLocations.length && allowedLocations[0].code) {
    // Setting state during render is allowed when guarded by a condition that
    // will be false next render — same pattern React docs recommend for
    // deriving state from props. See https://react.dev/reference/react/useState
    setLocationCode(allowedLocations[0].code);
  }

  // Load slip when editing
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const s = await getPackingSlip(editId);
        setInvoiceNo(s.invoice_no);
        setLocationCode(s.location_code);
        setToLocationName(s.to_location_name || "");
        setItems(s.items.length ? s.items : [blankItem()]);
        setSavedSlip({ id: s.id, slip_number: s.slip_number, dispatched: s.dispatched });
      } catch {
        toast.error("Failed to load packing slip");
      }
    })();
  }, [editId]);

  const isLocked = savedSlip?.dispatched === true;

  const summary = useMemo(() => {
    const totalItems = items.filter((i) => i.part_number || i.part_name).length;
    const totalQty = items.reduce((s, i) => s + (Number(i.qty) || 0), 0);
    const boxes = new Set();
    items.forEach((i) =>
      (i.box_no || "")
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean)
        .forEach((b) => boxes.add(b))
    );
    return { totalItems, totalQty, totalBoxes: boxes.size };
  }, [items]);

  const setItemField = (idx, key, value) => {
    setItems((arr) => {
      const copy = [...arr];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const onPartNumberChange = (idx, value) => {
    setItemField(idx, "part_number", value.toUpperCase());
    // Clear lookup state while user is typing; the actual fetch happens on blur
    setRowLookupState((s) => {
      const c = { ...s };
      delete c[items[idx].id];
      return c;
    });
    // Cheap synchronous match — if the exact key already exists in our map,
    // fill name immediately. (Slower server fallback runs on blur.)
    const key = (value || "").trim().toUpperCase();
    const localHit = partsIndex.get(key);
    if (localHit) {
      setItemField(idx, "part_name", localHit.part_name);
      setRowLookupState((s) => ({ ...s, [items[idx].id]: "ok" }));
    }
  };

  const onPartNumberBlur = (idx) => {
    const row = items[idx];
    if (!row || !row.part_number) return;
    // If a part_name already auto-filled correctly we're done.
    if (row.part_name && rowLookupState[row.id] === "ok") return;
    // Trigger the full resolver (local map → server fallback).
    resolvePartName(row.id, row.part_number);
  };

  const pickPartSuggestion = (idx, p) => {
    setItems((arr) => {
      const copy = [...arr];
      copy[idx] = {
        ...copy[idx],
        part_number: p.part_number,
        part_name: p.part_name,
      };
      return copy;
    });
    setRowLookupState((s) => ({ ...s, [items[idx].id]: "ok" }));
  };

  const addRow = (focusIdx) => {
    setItems((arr) => [...arr, blankItem()]);
    setTimeout(() => {
      const next = (focusIdx ?? items.length) + 0; // new row appended at end
      const refRow = rowRefs.current[items.length];
      const firstInput = refRow?.querySelector('input[data-field="part_number"]');
      firstInput?.focus();
    }, 30);
  };

  const removeRow = (idx) => {
    if (items.length === 1) {
      setItems([blankItem()]);
      return;
    }
    setItems((arr) => arr.filter((_, i) => i !== idx));
  };

  // Tab on the last row's Box No → add new row
  const handleBoxKeyDown = (idx, e) => {
    if (e.key === "Tab" && !e.shiftKey && idx === items.length - 1) {
      const value = e.target.value;
      // Only auto-add if the row has at least part_number filled
      if (items[idx].part_number) {
        e.preventDefault();
        // Normalise box and ensure state updates first
        setItemField(idx, "box_no", normaliseBox(value));
        addRow(idx + 1);
      }
    }
  };

  // Stateless validator over a given items array (allows post-resolve checks).
  const validateItems = (rows) => {
    if (!invoiceNo.trim()) return "Invoice No is required.";
    if (!locationCode) return "From Location is required.";
    if (!toLocationName.trim()) return "To Location is required.";
    const real = rows.filter((i) => i.part_number || i.part_name || i.box_no);
    if (real.length === 0) return "Add at least one part.";
    for (let i = 0; i < real.length; i++) {
      const it = real[i];
      if (!it.part_number) return `Row ${i + 1}: Part No is required.`;
      if (!it.part_name)
        return `Row ${i + 1}: Part Name is missing — Part Number "${it.part_number}" not found in Parts Master.`;
      if (!(Number(it.qty) > 0)) return `Row ${i + 1}: Qty must be > 0.`;
      if (!it.box_no || !normaliseBox(it.box_no))
        return `Row ${i + 1}: Box No is required.`;
    }
    return null;
  };

  const persist = async () => {
    // Save-time re-validation: for every row with a Part Number but missing
    // Part Name, run a fresh resolver (local → server) in parallel. This
    // guarantees that even if the master loaded slowly or a row was added
    // before the cache was ready, we still resolve right before saving.
    const toResolve = items.filter(
      (i) => i.part_number && i.part_number.trim() && !i.part_name
    );

    // Build a fresh snapshot of items reflecting any resolutions.
    let snapshot = items;
    if (toResolve.length > 0) {
      const resolved = await Promise.all(
        toResolve.map(async (row) => {
          const key = row.part_number.trim().toUpperCase();
          // Local map first
          const local = partsIndex.get(key);
          if (local) return { id: row.id, part_name: local.part_name };
          // Server fallback
          const doc = await lookupOnServer(key);
          return { id: row.id, part_name: doc?.part_name || "" };
        })
      );
      const patch = Object.fromEntries(resolved.map((r) => [r.id, r.part_name]));
      snapshot = items.map((i) =>
        patch[i.id] !== undefined && patch[i.id] ? { ...i, part_name: patch[i.id] } : i
      );
      setItems(snapshot);
      // Update row lookup states to reflect what we found.
      setRowLookupState((s) => {
        const c = { ...s };
        for (const r of resolved) {
          c[r.id] = r.part_name ? "ok" : "missing";
        }
        return c;
      });
    }

    const err = validateItems(snapshot);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const cleanItems = snapshot
        .filter((i) => i.part_number)
        .map((i) => ({
          part_number: i.part_number.toUpperCase().trim(),
          part_name: i.part_name,
          qty: Number(i.qty),
          box_no: normaliseBox(i.box_no),
        }));
      let res;
      if (savedSlip?.id) {
        res = await updatePackingSlip(savedSlip.id, {
          invoice_no: invoiceNo,
          to_location_name: toLocationName,
          items: cleanItems,
          actor: currentUser.name,
          actor_role: currentUser.role,
        });
        toast.success(`Packing Slip ${res.slip_number} updated`);
      } else {
        res = await createPackingSlip({
          invoice_no: invoiceNo,
          location_code: locationCode,
          to_location_name: toLocationName,
          items: cleanItems,
          actor: currentUser.name,
          actor_role: currentUser.role,
        });
        toast.success(`Packing Slip ${res.slip_number} saved`);
      }
      setSavedSlip({ id: res.id, slip_number: res.slip_number, dispatched: res.dispatched });
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save packing slip");
    } finally {
      setSaving(false);
    }
  };

  const onSaveAndPrint = async () => {
    const res = await persist();
    if (res?.id) {
      window.open(`/packing-slip/${res.id}/print`, "_blank", "noopener");
    }
  };

  // Mark the saved slip as dispatched from this screen — saves a trip to
  // Saved Packing Slips. Only callable once the slip exists (savedSlip.id)
  // and isn't already dispatched.
  const onMarkDispatched = async () => {
    if (!savedSlip?.id || savedSlip.dispatched) return;
    setDispatching(true);
    try {
      const updated = await dispatchPackingSlip(
        savedSlip.id,
        currentUser.name,
        currentUser.role,
      );
      setSavedSlip({
        id: updated.id,
        slip_number: updated.slip_number,
        dispatched: true,
      });
      toast.success(`${updated.slip_number} marked as dispatched`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally {
      setDispatching(false);
      setConfirmDispatchOpen(false);
    }
  };

  // Filter parts suggestions
  const suggestionsFor = (q) => {
    if (!q || q.length < 1) return [];
    const ql = q.toLowerCase();
    return partsMaster
      .filter(
        (p) =>
          p.part_number.toLowerCase().includes(ql) ||
          p.part_name.toLowerCase().includes(ql)
      )
      .slice(0, 6);
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="new-packing-slip-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
            <PackageOpen className="w-6 h-6 text-yamaha-700" />
          </div>
          <div>
            <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
              {editId ? `Edit Packing Slip${savedSlip?.slip_number ? ` · ${savedSlip.slip_number}` : ""}` : "New Packing Slip"}
            </h1>
            <p className="text-[12.5px] text-[#747684] mt-1">
              YBC spare parts dispatch · packed parts recorded invoice-wise.
            </p>
          </div>
        </div>
        <div className="text-right text-[11px] text-[#747684] leading-snug">
          <div className="font-semibold text-[#1a1b22]">{currentUser?.name}</div>
          <div>{new Date().toLocaleString("en-GB")} IST</div>
        </div>
      </div>

      {isLocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-[12.5px] text-amber-800" data-testid="dispatched-banner">
          This packing slip has been <span className="font-semibold">dispatched</span> and is locked for editing.
        </div>
      )}

      {/* Header fields */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Invoice No <span className="text-rose-600">*</span>
            </label>
            <input
              data-testid="invoice-no-input"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value.toUpperCase())}
              disabled={isLocked}
              placeholder="INV-2026-0001"
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] uppercase disabled:bg-[#f4f3fc]"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Packing Date
            </label>
            <input
              value={new Date().toLocaleDateString("en-GB")}
              disabled
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] bg-[#f4f3fc] text-[#444653]"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Created By
            </label>
            <input
              value={currentUser?.name || ""}
              disabled
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] bg-[#f4f3fc] text-[#444653]"
            />
          </div>
        </div>
        {/* From / To Location row — From = admin dropdown (masters); To = free text. */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              From Location <span className="text-rose-600">*</span>
            </label>
            <select
              data-testid="from-location-select"
              value={locationCode}
              onChange={(e) => setLocationCode(e.target.value)}
              disabled={isLocked || !!editId}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] disabled:bg-[#f4f3fc]"
            >
              <option value="">— Select From Location —</option>
              {allowedLocations.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              To Location <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              data-testid="to-location-input"
              value={toLocationName}
              onChange={(e) => setToLocationName(e.target.value)}
              disabled={isLocked}
              placeholder="Type destination (e.g. Customer, Branch, Party name)"
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] disabled:bg-[#f4f3fc]"
            />
          </div>
        </div>
      </div>

      {/* Part entry rows */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
          <div className="text-[13px] font-semibold text-[#1a1b22] flex items-center gap-2">
            <Package className="w-4 h-4 text-yamaha-700" /> Packing Items
          </div>
          <div className="text-[11px] text-[#747684]">Press <span className="font-mono bg-[#f4f3fc] px-1.5 rounded">Tab</span> on Box No to add a new row</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="items-table">
            <thead className="bg-[#faf8ff]">
              <tr>
                <th className="px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[28%]">Part No *</th>
                <th className="px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[36%]">Part Name</th>
                <th className="px-3 py-2 text-right text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[10%]">Qty *</th>
                <th className="px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[18%]">Box No *</th>
                <th className="px-3 py-2 text-center text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[8%]">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <PartRow
                  key={it.id}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  idx={idx}
                  item={it}
                  disabled={isLocked}
                  suggestions={suggestionsFor(it.part_number)}
                  lookupState={rowLookupState[it.id]}
                  onPartNumberChange={onPartNumberChange}
                  onPartNumberBlur={() => onPartNumberBlur(idx)}
                  onSetField={setItemField}
                  onPickSuggestion={pickPartSuggestion}
                  onRemove={() => removeRow(idx)}
                  onBoxKeyDown={(e) => handleBoxKeyDown(idx, e)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!isLocked && (
          <div className="px-4 py-2.5 border-t border-[#e2e1eb] bg-[#faf8ff]">
            <button
              onClick={() => addRow(items.length)}
              data-testid="add-row-btn"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-yamaha-700 hover:text-yamaha-800"
            >
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          </div>
        )}
      </div>

      {/* Footer summary */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Total Items" value={summary.totalItems} testid="summary-items" />
        <SummaryCard label="Total Quantity" value={summary.totalQty} testid="summary-qty" />
        <SummaryCard label="Total No Of Boxes" value={summary.totalBoxes} testid="summary-boxes" />
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-[#faf8ff] py-3 -mx-4 px-4 border-t border-[#e2e1eb]">
        <Button
          variant="outline"
          onClick={() => navigate("/packing-slips")}
          data-testid="cancel-btn"
        >
          Cancel
        </Button>
        {!isLocked && (
          <Button
            onClick={persist}
            disabled={saving}
            data-testid="save-btn"
            className="bg-yamaha-700 hover:bg-yamaha-800"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : savedSlip ? "Update" : "Save Packing Slip"}
          </Button>
        )}
        {savedSlip?.id && (
          <Button
            onClick={() => window.open(`/packing-slip/${savedSlip.id}/print`, "_blank", "noopener")}
            data-testid="print-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        )}
        {savedSlip?.id && !savedSlip.dispatched && (
          <Button
            onClick={() => setConfirmDispatchOpen(true)}
            disabled={dispatching}
            data-testid="mark-dispatched-btn"
            title="Mark this packing slip as dispatched"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Truck className="w-4 h-4 mr-2" />
            {dispatching ? "Marking…" : "Mark Dispatched"}
          </Button>
        )}
        {!savedSlip && !isLocked && (
          <Button
            onClick={onSaveAndPrint}
            disabled={saving}
            data-testid="save-print-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Save & Print
          </Button>
        )}
      </div>

      {/* Mark Dispatched confirmation */}
      <AlertDialog
        open={confirmDispatchOpen}
        onOpenChange={(v) => !v && setConfirmDispatchOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark {savedSlip?.slip_number || "this packing slip"} as dispatched?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Once marked dispatched, this packing slip will be <strong>locked</strong>{" "}
              and cannot be edited or deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dispatch-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onMarkDispatched}
              data-testid="confirm-dispatch-yes"
              className="bg-amber-600 hover:bg-amber-700"
            >
              Yes, Mark Dispatched
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const SummaryCard = ({ label, value, testid }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl px-4 py-3" data-testid={testid}>
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684]">{label}</div>
    <div className="text-[24px] font-semibold text-yamaha-800 mt-0.5">{String(value).padStart(2, "0")}</div>
  </div>
);

// Forward ref so the parent can focus a row's first input after appending
const PartRow = React.forwardRef(({
  idx, item, disabled, suggestions, lookupState, onPartNumberChange, onPartNumberBlur,
  onSetField, onPickSuggestion, onRemove, onBoxKeyDown,
}, ref) => {
  const [focusField, setFocusField] = useState(null);
  const showSuggestions =
    focusField === "part_number" && item.part_number && suggestions.length > 0;
  return (
    <tr ref={ref} className="border-t border-[#e2e1eb]">
      <td className="px-3 py-1.5 relative">
        <input
          data-field="part_number"
          data-testid={`row-${idx}-part-number`}
          value={item.part_number}
          onChange={(e) => onPartNumberChange(idx, e.target.value)}
          onFocus={() => setFocusField("part_number")}
          onBlur={() => {
            setTimeout(() => setFocusField(null), 150);
            onPartNumberBlur && onPartNumberBlur();
          }}
          disabled={disabled}
          placeholder="e.g. 5BR-F1641-00"
          className={`w-full border rounded px-2 py-1.5 text-[12.5px] uppercase font-mono disabled:bg-[#f4f3fc] ${
            lookupState === "missing" ? "border-rose-400" : "border-[#e2e1eb]"
          }`}
        />
        {showSuggestions && (
          <div className="absolute z-30 left-3 right-3 top-full mt-1 bg-white border border-[#e2e1eb] rounded-md shadow-lg max-h-56 overflow-y-auto">
            {suggestions.map((p) => (
              <button
                key={p.part_number}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPickSuggestion(idx, p); }}
                data-testid={`row-${idx}-suggest-${p.part_number}`}
                className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-yamaha-50"
              >
                <div className="font-mono text-yamaha-700 font-semibold">{p.part_number}</div>
                <div className="text-[11px] text-[#444653] truncate">{p.part_name}</div>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5">
        <input
          value={item.part_name}
          onChange={(e) => onSetField(idx, "part_name", e.target.value)}
          disabled={disabled}
          placeholder={
            lookupState === "loading"
              ? "Looking up…"
              : lookupState === "missing"
              ? "Part Number not found in Parts Master"
              : "Auto-fetched from Parts Master"
          }
          data-testid={`row-${idx}-part-name`}
          className={`w-full border rounded px-2 py-1.5 text-[12.5px] disabled:bg-[#f4f3fc] ${
            lookupState === "missing"
              ? "border-rose-400 placeholder:text-rose-600 placeholder:font-medium"
              : "border-[#e2e1eb]"
          }`}
        />
        {lookupState === "missing" && (
          <div
            className="text-[10.5px] text-rose-600 mt-0.5 font-medium"
            data-testid={`row-${idx}-missing`}
          >
            Part Number not found in Parts Master
          </div>
        )}
      </td>
      <td className="px-3 py-1.5">
        <input
          type="number"
          min="1"
          value={item.qty}
          onChange={(e) => onSetField(idx, "qty", e.target.value)}
          disabled={disabled}
          data-testid={`row-${idx}-qty`}
          className="w-full border border-[#e2e1eb] rounded px-2 py-1.5 text-[12.5px] text-right disabled:bg-[#f4f3fc]"
        />
      </td>
      <td className="px-3 py-1.5">
        <input
          value={item.box_no}
          onChange={(e) => onSetField(idx, "box_no", e.target.value.replace(/[^0-9,]/g, ""))}
          onBlur={(e) => onSetField(idx, "box_no", normaliseBox(e.target.value))}
          onKeyDown={onBoxKeyDown}
          disabled={disabled}
          placeholder="01 or 01,02"
          data-testid={`row-${idx}-box-no`}
          className="w-full border border-[#e2e1eb] rounded px-2 py-1.5 text-[12.5px] font-mono disabled:bg-[#f4f3fc]"
        />
      </td>
      <td className="px-3 py-1.5 text-center">
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          data-testid={`row-${idx}-remove`}
          className="p-1.5 rounded hover:bg-rose-50 text-rose-600 disabled:text-[#c4c5d5] disabled:hover:bg-transparent"
          title="Remove row"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
});
PartRow.displayName = "PartRow";

export default NewPackingSlip;
